import AgentCommonLayout from "containers/AgentService/AccountPage/AgentCommonLayout";
import { AuthContext } from "context/appContext";
import { ProfilService } from "data/types";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addService,
  initState,
  nextStep,
  previousStep,
  resetValues,
  setServiceData,
} from "redux/features/service/serviceSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ServiceDetails from "./ServiceDetails";
import ServiceExtraDetails from "./ServiceExtraDetails";
import ServiceFilesUpload from "./ServiceFilesUpload";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  PhotoIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";

import { socket } from "utils/socket";

const ServiceCommonLayout: React.FC = () => {
  const authContext = useContext(AuthContext);

  let role = authContext.user?.role ?? "";
  let profile = authContext.user?.profil as ProfilService;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const serviceState = useAppSelector((state) => state.service);
  const progress = (serviceState.currentStep / serviceState.totalSteps) * 100;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [autoNext, setAutoNext] = useState<boolean>(false);
  const [event, setEvent] = useState<Event>(new Event(""));

  const initialRender = useRef(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    dispatch(initState());
  }, []);

  useEffect(() => {
    switch (serviceState.loading) {
      case "pending": {
        setIsLoading(true);

        break;
      }
      case "succeeded": {
        setIsLoading(false);
        if (serviceState.serviceData && serviceState.currentStep === 3) {
          dispatch(addService(serviceState.serviceData));
        } else {
          dispatch(nextStep());
        }
        break;
      }
      case "failed": {
        setIsLoading(false);
        break;
      }
    }
  }, [serviceState.loading]);

  useEffect(() => {
    const handleAddService = async () => {
      if (serviceState.serviceData && serviceState.currentStep === 3) {
        const result = await dispatch(addService(serviceState.serviceData));
        socket.emit("send-notification", {
          action: "create_prestation",
          prestation: serviceState.serviceData,
          prestationSenderId: serviceState.serviceData?.serviceAgentId,
          prestationId: result?.payload?.id,
        });
        dispatch(resetValues());
        navigate("/prestataire/services-list");
      }
    };
    handleAddService();
  }, [serviceState.serviceData]);

  useEffect(() => {
    if (
      !initialRender.current &&
      serviceState.serviceData &&
      serviceState.currentStep < serviceState.totalSteps &&
      autoNext
    ) {
      dispatch(nextStep());
      setIsLoading(false);
    }

    initialRender.current = false;
  }, [serviceState.serviceData, autoNext]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceState.currentStep]);

  const handleNextClick = () => {
    switch (serviceState.currentStep) {
      case 1: {
        setEvent(new Event("chekServiceFormOne"));
        setAutoNext(true);
        break;
      }
      case 2: {
        setEvent(new Event("chekServiceFormTwo"));
        setAutoNext(true);
        break;
      }
      case 3: {
        setEvent(new Event("chekServiceFormThree"));
        setAutoNext(true);
        break;
      }

      default: {
        setEvent(new Event(""));
        break;
      }
    }
  };

  const handlePreviousClick = () => {
    setAutoNext(false);
    dispatch(previousStep());
  };

  const getStepIcon = (step: number, currentStep: number) => {
    const isCompleted = step < currentStep;
    const isCurrent = step === currentStep;

    const iconClass = `w-6 h-6 ${
      isCompleted ? "text-green-600" : isCurrent ? "text-eventToBee" : "text-gray-400"
    }`;

    if (isCompleted) {
      return <CheckCircleIcon className={iconClass} />;
    }

    switch (step) {
      case 1:
        return <ClipboardDocumentListIcon className={iconClass} />;
      case 2:
        return <Cog6ToothIcon className={iconClass} />;
      case 3:
        return <PhotoIcon className={iconClass} />;
      default:
        return null;
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1:
        return "Informations générales";
      case 2:
        return "Spécifications";
      case 3:
        return "Documents & Images";
      default:
        return "";
    }
  };

  return (
    <AgentCommonLayout>
      <div className={`px-4 pb-24 mx-auto max-w-4xl nc-PageAddListing1 lg:pb-32`} data-nc-id="PageAddListing1">
        {/* Header moderne */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Créer une nouvelle prestation</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Bonjour <span className="font-semibold text-eventToBee">{profile?.designation}</span>, créons ensemble
            votre prestation !
          </p>
        </div>

        {/* Stepper moderne */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3].map((step, index) => (
              <div key={step} className="flex items-center flex-1">
                <div className="flex items-center">
                  <div
                    className={`
                    flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300
                    ${
                      step < serviceState.currentStep
                        ? "bg-green-100 border-green-500"
                        : step === serviceState.currentStep
                          ? "bg-eventToBee border-eventToBee text-white"
                          : "bg-white border-gray-300"
                    }
                  `}
                  >
                    {getStepIcon(step, serviceState.currentStep)}
                  </div>
                  <div className="ml-4 hidden sm:block">
                    <p
                      className={`text-sm font-medium ${
                        step <= serviceState.currentStep ? "text-eventToBee" : "text-gray-500"
                      }`}
                    >
                      Étape {step}
                    </p>
                    <p
                      className={`text-xs ${
                        step <= serviceState.currentStep ? "text-gray-700 dark:text-gray-200" : "text-gray-400"
                      }`}
                    >
                      {getStepTitle(step)}
                    </p>
                  </div>
                </div>
                {index < 2 && (
                  <div
                    className={`
                    flex-1 h-0.5 mx-4 transition-all duration-300
                    ${step < serviceState.currentStep ? "bg-green-500" : "bg-gray-200"}
                  `}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contenu principal avec animation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 mb-8">
          <AnimatePresence mode="wait">
            {serviceState.currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ServiceDetails
                  isLoading={isLoading}
                  role={role ?? ""}
                  handleForm={event}
                  FormData={(data) => {
                    dispatch(setServiceData({ ...serviceState.serviceData, ...data }));
                  }}
                  errorServiceForm={serviceState.serviceError}
                />
              </motion.div>
            )}

            {serviceState.currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ServiceExtraDetails
                  isLoading={isLoading}
                  role={role ?? ""}
                  handleForm={event}
                  FormData={(data) => dispatch(setServiceData({ ...serviceState.serviceData, ...data }))}
                  errorServiceForm={serviceState.serviceError}
                />
              </motion.div>
            )}

            {serviceState.currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ServiceFilesUpload
                  isLoading={isLoading}
                  role={role ?? ""}
                  handleForm={event}
                  FormData={(data) => dispatch(setServiceData({ ...serviceState.serviceData, ...data }))}
                  errorServiceForm={serviceState.serviceError}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Barre de progression moderne */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Progression: {Math.round(progress)}%
            </span>
            <span className="text-sm text-gray-500">
              Étape {serviceState.currentStep} sur {serviceState.totalSteps}
            </span>
          </div>

          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-6">
            <motion.div
              className="bg-gradient-to-r from-eventToBee to-eventToBeePink h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            ></motion.div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <ButtonSecondary
              onClick={handlePreviousClick}
              disabled={serviceState.currentStep <= 1}
              className="flex items-center space-x-2"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>Retour</span>
            </ButtonSecondary>

            <ButtonPrimary
              onClick={handleNextClick}
              loading={isLoading}
              disabled={isLoading}
              className="flex items-center space-x-2"
            >
              <span>{serviceState.currentStep === serviceState.totalSteps ? "Finaliser" : "Continuer"}</span>
              <ArrowRightIcon className="w-4 h-4" />
            </ButtonPrimary>
          </div>
        </div>
      </div>
    </AgentCommonLayout>
  );
};

export default ServiceCommonLayout;
